import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { PermsContext } from "../perms";
import { downloadPath } from "../download";

const tone = (n) => (n >= 80 ? "ok" : n >= 60 ? "warn" : "danger");
const color = (n) => (n >= 80 ? "#16a34a" : n >= 60 ? "#d97706" : "#dc2626");

function Gauge({ value }) {
  const r = 54, c = 2 * Math.PI * r, off = c - (value / 100) * c;
  return (
    <svg width="140" height="140" viewBox="0 0 140 140">
      <circle cx="70" cy="70" r={r} fill="none" stroke="#e2e8f0" strokeWidth="12" />
      <circle cx="70" cy="70" r={r} fill="none" stroke={color(value)} strokeWidth="12" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 70 70)" />
      <text x="70" y="66" textAnchor="middle" fontSize="30" fontWeight="800" fill="#0f172a">{value}</text>
      <text x="70" y="88" textAnchor="middle" fontSize="11" fill="#64748b">of 100</text>
    </svg>
  );
}

const SG_COLOR = { Pass: "#16a34a", Warn: "#d97706", Fail: "#dc2626", NoData: "#64748b" };

function Safeguards({ sg, onChanged }) {
  const { actions } = useContext(PermsContext);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const sync = async () => {
    setBusy(true); setMsg("");
    try {
      const r = (await api.post("/compliance/safeguards/sync")).data?.data;
      setMsg(`Updated ${r.controls} controls and ${r.obligations} obligations; risks: ${r.risksOpened} opened, ${r.risksUpdated} updated, ${r.risksClosed} closed.`);
      onChanged();
    } catch (e) { setMsg(e?.response?.data?.message || e.message); }
    setBusy(false);
  };
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="toolbar">
        <b>Technical safeguards (automated from discovery data)</b>
        <span className="sub" style={{ marginLeft: 8 }}>
          {sg ? (sg.score == null ? "no data yet" : `${sg.summary.pass} pass / ${sg.summary.warn} warn / ${sg.summary.fail} fail`) : "loading…"}
        </span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {actions.create && <button className="btn sm" disabled={busy} onClick={sync} title="Writes results into Control Library, Regulatory Obligations and the Risk Register">Sync controls, obligations &amp; risks</button>}
          {actions.export && <button className="btn sm" onClick={() => downloadPath("/compliance/safeguards/evidence", "safeguards-evidence.zip").catch((e) => setMsg(e.message))}>Download evidence ZIP</button>}
        </span>
      </div>
      {msg && <div className="sub" style={{ padding: "6px 12px" }}>{msg}</div>}
      <div className="tablewrap">
        <table>
          <thead><tr><th>Status</th><th>Check</th><th>Result</th><th>Detail</th><th>Regulation</th></tr></thead>
          <tbody>
            {(sg?.checks || []).map((c) => (
              <tr key={c.key}>
                <td><span className="pill" style={{ color: SG_COLOR[c.status], fontWeight: 700 }}>{c.status === "NoData" ? "No data" : c.status}</span></td>
                <td>{c.title}<div className="sub">{c.key} · {c.severity}</div></td>
                <td>{c.value == null ? "—" : `${c.value}${c.unit === "%" ? "%" : ""}`}<div className="sub">{c.target ? `target ${c.target}` : ""}</div></td>
                <td style={{ color: "#64748b" }}>{c.detail}</td>
                <td className="sub">{c.refs.map((r) => <div key={r}>{r}</div>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="sub" style={{ padding: 12 }}>Clause references come from secondary summaries; verify against the official DPDP Rules 2025 and RBI texts before external use.</div>
    </div>
  );
}

export default function Compliance() {
  const [d, setD] = useState(null);
  const [sg, setSg] = useState(null);
  const [err, setErr] = useState("");
  const load = () => {
    api.get("/compliance/score").then((r) => setD(r.data?.data)).catch((e) => setErr(e?.response?.data?.message || "Failed to load"));
    api.get("/compliance/safeguards").then((r) => setSg(r.data?.data)).catch(() => setSg(null));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <h1>Compliance Score</h1>
      <p className="sub">Organisation-wide DPDP compliance, computed from live data across every register.</p>
      {err && <div className="err">{err}</div>}
      {!d ? <div className="card"><div className="empty">Loading…</div></div> : (
        <>
          <div className="card" style={{ padding: 20, marginBottom: 16, display: "flex", gap: 24, alignItems: "center" }}>
            <Gauge value={d.overall} />
            <div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>Overall: {d.overall}/100</div>
              <div style={{ marginTop: 6 }}><span className={`pill ${tone(d.overall)}`}>{d.level}</span></div>
              <div style={{ color: "#64748b", marginTop: 10, fontSize: 13 }}>
                Compliance checklist: <b>{d.checklist?.compliant}/{d.checklist?.total}</b> compliant ({d.checklist?.pct}%).{" "}
                <Link to="/m/checklist">Update checklist →</Link>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="toolbar"><b>Score by area (weighted)</b></div>
            <div className="tablewrap">
              <table>
                <thead><tr><th>Area</th><th>Weight</th><th style={{ width: 260 }}>Score</th><th>Detail</th></tr></thead>
                <tbody>
                  {d.areas.map((a) => (
                    <tr key={a.name}>
                      <td>{a.name}</td>
                      <td>{a.weight}%</td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 6, height: 12 }}>
                            <div style={{ width: `${a.score}%`, background: color(a.score), height: 12, borderRadius: 6 }} />
                          </div>
                          <b style={{ width: 34, textAlign: "right" }}>{a.score}</b>
                        </div>
                      </td>
                      <td style={{ color: "#64748b" }}>{a.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Safeguards sg={sg} onChanged={load} />
        </>
      )}
    </div>
  );
}
